// @ts-nocheck
import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { styled, createTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {
    Avatar,
    Collapse,
    ThemeProvider,
    useMediaQuery
} from '@mui/material';

import {
    Menu as MenuIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    Logout,
    Home,
    HomeOutlined
} from '@mui/icons-material';

import { selectUserProfile } from '../../redux/actions/users';
import { useSelector } from '../../redux/hooks';

import VerificationBanner from './VerificationBanner';
import getMenuLinks from './menuLinks';
import {
    COLOR_BLUE,
    COLOR_MINT,
    COLOR_SURFACE,
    COLOR_TEAL,
    COLOR_WHITE,
    COLOR_NAV_HOVER,
    COLOR_NAV_ACTIVE,
    FONT_FAMILY_SANS
} from '../../theme';
import logout from '../../utils/logout';
import NotificationsMenuItem from './NotificationsMenuItem';
import RoutesOnboarding from '../TenantRoutes/RoutesOnboarding';

const APP_BAR_MD_HEIGHT = 64;
const APP_BAR_SM_HEIGHT = 56;
const VERIFICATION_BANNER_HEIGHT = 55;
const MENU_DRAWER_WIDTH = 300;
const MENU_ITEM_SX = {
    mx: 1,
    my: 0.5,
    py: 1,
    pl: 2,
    borderRadius: 12,
    transition: 'background 0.15s ease',
    '&:hover': {
        background: COLOR_NAV_HOVER
    }
};

const openedMixin = (theme: any) => ({
    width: MENU_DRAWER_WIDTH,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: any) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(9)} + 1px)`,
    // width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.down('md')]: {
        width: 0,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: MENU_DRAWER_WIDTH,
        width: `calc(100% - ${MENU_DRAWER_WIDTH}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer)(
    ({ theme, open }) => ({
        width: MENU_DRAWER_WIDTH,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': {
                ...closedMixin(theme),
                '& .MuiListItemText-root': {
                    opacity: 0,
                },
            },
        }),
        // Overlay on small screens so the menu does not push the page wider than the viewport.
        [theme.breakpoints.down('md')]: {
            width: 0,
            '& .MuiDrawer-paper': {
                width: MENU_DRAWER_WIDTH,
                overflowX: 'hidden',
            },
        },
    }),
);

const theme = createTheme({
    palette: {
        background: {
            paper: COLOR_BLUE,
            active: COLOR_NAV_ACTIVE,
        },
        text: {
            primary: COLOR_SURFACE,
            active: '#FFFFFF'
        },
        primary: {
            main: COLOR_TEAL
        },
        secondary: {
            main: COLOR_MINT
        },
        job: {
            main: COLOR_TEAL,
            contrastText: COLOR_WHITE
        }
    },
    typography: {
        fontFamily: FONT_FAMILY_SANS
    },
    components: {
        MuiListItemIcon: {
            styleOverrides: {
                root: { minWidth: 40}
            }
        }
    }

});

export default function Layout() {
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [open, setOpen] = React.useState(isDesktop);
    const [openSubmenus, setOpenSubmenus] = React.useState<Record<number, boolean>>({});
    const navigate = useNavigate();

    React.useEffect(() => {
        setOpen(isDesktop);
    }, [isDesktop]);

    const userProfile = useSelector(selectUserProfile());
    const [dismissVerificationBanner, setDismissVerificationBanner] = React.useState(false);
    const showVerificationBanner = React.useMemo(() => (
        !dismissVerificationBanner && (
            userProfile?.emailVerified === false
            || (
                userProfile.phone
                && userProfile?.phoneVerified === false
            )
        )
    ), [dismissVerificationBanner, userProfile]);

    const toggleDrawer = () => {
        setOpen((current) => !current);
    };

    const { pathname } = useLocation();

    React.useEffect(() => {
        if (!isDesktop) {
            setOpen(false);
        }
    }, [pathname, isDesktop]);

    const links = React.useMemo(
        () => getMenuLinks({
            isAdmin: userProfile?.isAdmin,
            isCarrier: userProfile?.isCarrier
        }),
        [userProfile?.isAdmin, userProfile?.isCarrier]
    );

    const isActiveLink = React.useCallback((linkPath: string) => {
        return (
            linkPath === '/'
                ? pathname === linkPath
                : (
                    pathname?.startsWith(linkPath)
                    || (pathname?.startsWith('/capacities') && linkPath?.startsWith('/jobs'))
                )
        );
    }, [pathname]);

    const getTextColor = React.useCallback((linkPath: string) => {
        return isActiveLink(linkPath) ? theme.palette.text.active : theme.palette.text.primary;
    }, [isActiveLink]);

    const getBackgroundColor = React.useCallback((linkPath: string) => {
        return isActiveLink(linkPath) ? theme.palette.background.active : 'transparent';
    }, [isActiveLink]);

    const getMenuItemSx = React.useCallback((linkPath: string, extra = {}) => ({
        ...MENU_ITEM_SX,
        ...extra,
        background: getBackgroundColor(linkPath),
        '&:hover': {
            background: isActiveLink(linkPath) ? getBackgroundColor(linkPath) : COLOR_NAV_HOVER
        }
    }), [getBackgroundColor, isActiveLink]);

    return (
        <Box sx={{ display: 'flex', width: '100%', maxWidth: '100%' }}>
            <AppBar
                position="fixed"
                open={isDesktop && open}
                sx={{
                    display: 'none',
                    bgcolor: COLOR_BLUE,
                    backgroundImage: 'none',
                    [theme.breakpoints.down('md')]: {
                        display: 'block',
                    }
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        sx={{
                            marginRight: '36px',
                            ...(open && { display: 'none' }),
                        }}
                        onClick={toggleDrawer}
                    >
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <ThemeProvider theme={theme}>
                <Drawer
                    variant={isDesktop ? 'permanent' : 'temporary'}
                    open={open}
                    onClose={() => setOpen(false)}
                    ModalProps={{ keepMounted: true }}
                >
                    <DrawerHeader
                        onClick={toggleDrawer}
                        sx={{
                            justifyContent: 'space-between',
                            ':hover': { cursor: 'pointer' }
                        }}
                    >
                        <Avatar
                            variant="square"
                            sx={{
                                m: 1,
                                width: 40,
                                height: 40
                            }}
                            src="/logiclink_logo_white.png"
                        />
                        <IconButton sx={{ color: theme.palette.text.primary }}>
                            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                        </IconButton>
                    </DrawerHeader>

                    <List disablePadding>
                        <ListItemButton
                            onClick={() => navigate('/')}
                            sx={getMenuItemSx('/')}
                        >
                            <ListItemIcon sx={{ color: getTextColor('/') }}>
                                <HomeOutlined />
                            </ListItemIcon>
                            <ListItemText primary="Hem" sx={{ color: getTextColor('/') }} />
                        </ListItemButton>

                        <NotificationsMenuItem
                            sx={MENU_ITEM_SX}
                            backgroundColor={getBackgroundColor('/notifications')}
                            textColor={getTextColor('/notifications')}
                            active={isActiveLink('/notifications')}
                            onClick={() => navigate('/notifications')}
                        />

                        {links.map((link) => (
                            link.links
                                ? (
                                    <React.Fragment key={link.name}>
                                        <ListItemButton
                                            onClick={() => setOpenSubmenus({
                                                ...openSubmenus,
                                                [link.key]: !openSubmenus[link.key]
                                            })}
                                            sx={MENU_ITEM_SX}
                                        >
                                            <ListItemIcon sx={{ color: getTextColor(link.path) }}>
                                                {link.icon}
                                            </ListItemIcon>
                                            <ListItemText primary={link.name} sx={{ color: getTextColor(link.path) }} />
                                            {openSubmenus[link.key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </ListItemButton>
                                        <Collapse in={openSubmenus[link.key]} timeout="auto" unmountOnExit>
                                            <List component="div" disablePadding>
                                                {link.links.map((sublink) => (
                                                    <ListItemButton
                                                        key={sublink.name}
                                                        onClick={() => (sublink.action ? sublink.action() : navigate(sublink.path))}
                                                        sx={getMenuItemSx(sublink.path, { pl: 4 })}
                                                    >
                                                        <ListItemIcon sx={{ color: getTextColor(sublink.path) }}>
                                                            {sublink.icon}
                                                        </ListItemIcon>
                                                        <ListItemText primary={sublink.name} sx={{ color: getTextColor(sublink.path) }} />
                                                    </ListItemButton>
                                                ))}
                                            </List>
                                        </Collapse>
                                    </React.Fragment>
                                ) : (
                                    <ListItemButton
                                        key={link.name}
                                        onClick={() => (link.action ? link.action() : navigate(link.path))}
                                        sx={getMenuItemSx(link.path)}
                                    >
                                        <ListItemIcon sx={{ color: getTextColor(link.path) }}>
                                            {link.icon}
                                        </ListItemIcon>
                                        <ListItemText primary={link.name} sx={{ color: getTextColor(link.path) }} />
                                    </ListItemButton>
                                )
                        ))}

                        <ListItemButton onClick={logout} sx={MENU_ITEM_SX}>
                            <ListItemIcon sx={{ color: theme.palette.text.primary }}>
                                <Logout />
                            </ListItemIcon>
                            <ListItemText primary="Logga ut" />
                        </ListItemButton>
                    </List>
                </Drawer>
            </ThemeProvider>

            <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', background: COLOR_SURFACE }}>
                <DrawerHeader
                    sx={{
                        display: 'none',
                        [theme.breakpoints.down('md')]: {
                            display: 'block',
                        }
                    }}
                />
                {/* <VerificationBanner /> */}
                <VerificationBanner
                    show={showVerificationBanner}
                    emailVerified={userProfile?.emailVerified}
                    phoneVerified={!userProfile?.phone || userProfile?.phoneVerified}
                    height={VERIFICATION_BANNER_HEIGHT}
                    verify={() => navigate('/user-profile')}
                    dismiss={() => setDismissVerificationBanner(true)}
                />
                <Box
                    sx={{
                        height: showVerificationBanner ? `calc(100vh - ${VERIFICATION_BANNER_HEIGHT}px)` : '100vh',
                        overflow: 'auto',
                        [theme.breakpoints.down('md')]: {
                            height: `calc(100vh - ${APP_BAR_MD_HEIGHT + (showVerificationBanner ? VERIFICATION_BANNER_HEIGHT : 0)}px)`
                        },
                        [theme.breakpoints.down('sm')]: {
                            height: `calc(100vh - ${APP_BAR_SM_HEIGHT + (showVerificationBanner ? VERIFICATION_BANNER_HEIGHT : 0)}px)`
                        }
                    }}
                >
                    <Outlet context={{ isLayoutOpen: open }} />
                </Box>
                <RoutesOnboarding />
            </Box>
        </Box>
    );
}
